import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ReusableSearcherComponent } from 'src/app/shared/reusable-searcher/reusable-searcher.component';
import { SnackbarService } from 'src/app/shared/snackbar/snackbar.service';
import { ProductsService } from 'src/app/services/products.service';
import { StoreDTO } from 'src/app/core/interfaces/stores.interface';
import { DRAWER_DATA } from 'src/app/app.component';
import { buildCsv, CsvColumn } from 'src/app/core/utils/download-csv';
import { StoreStockLotExportItem, StoreStockLotExportItemResponse } from 'src/app/pages/stock/services/store-stock-lots-export.model';
import { StoreStockLotsService } from 'src/app/pages/stock/services/store-stock-lots.service';
import { StockLotProduct } from 'src/app/core/interfaces/product.interface';
import { Subject, finalize, takeUntil } from 'rxjs';

type ExportType = 'all' | 'store' | 'product';

interface ExportStockDrawerData {
    isStorageDetailContext?: boolean;
    storeId?: number;
    storeName?: string;
    searchText?: string;
}

@Component({
    selector: 'app-export-stock-drawer',
    templateUrl: './export-stock-drawer.component.html',
    styleUrls: ['./export-stock-drawer.component.scss'],
})
export class ExportStockDrawerComponent implements OnInit, OnDestroy {
    title = '';

    selectedExportType: ExportType = 'all';

    selectedStores: StoreDTO[] = [];
    selectedProducts: StockLotProduct[] = [];

    availableStores: StoreDTO[] = [];
    availableProducts: StockLotProduct[] = [];

    isLoadingStores = false;
    isLoadingProducts = false;
    isExporting = false;
    hasAttemptedExport = false;

    isStorageDetailContext = false;
    currentStoreId: number | null = null;
    currentStoreName: string | null = null;
    currentSearchText: string | null = null;

    @ViewChild('storeSearcher') storeSearcher?: ReusableSearcherComponent;
    @ViewChild('productSearcher') productSearcher?: ReusableSearcherComponent;
    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly translateService: TranslateService,
        private readonly snackbarService: SnackbarService,
        private readonly storeStockLotsService: StoreStockLotsService,
        private readonly productsService: ProductsService,
        @Inject(DRAWER_DATA) private readonly data: ExportStockDrawerData
    ) { }

    ngOnInit(): void {
        this.title = this.translateService.instant('pages.stock.export.title');
        this.applyContext(this.data);
        this.loadStores();
        this.loadStockLotProducts();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onExportTypeChange(type: ExportType): void {
        if (this.isStorageDetailContext && type === 'store') return;

        this.selectedExportType = type;

        if (type === 'all') {
            this.selectedStores = [];
            this.selectedProducts = [];
            return;
        }

        if (type === 'store') {
            this.selectedProducts = [];
            return;
        }

        this.selectedStores = [];
    }

    onStoreSearch(query: string): void {
        const normalized = query.trim().toLowerCase();
        const results = normalized.length === 0
            ? this.availableStores
            : this.availableStores.filter(s => s.name.toLowerCase().includes(normalized));

        this.storeSearcher?.setResults(results);
    }

    onProductSearch(query: string): void {
        const normalized = query.trim().toLowerCase();

        if (normalized.length === 0) {
            this.productSearcher?.setResults(this.availableProducts);
            return;
        }

        const filtered = this.availableProducts.filter(product =>
            product.productName.toLowerCase().includes(normalized)
        );

        this.productSearcher?.setResults(filtered);
    }

    onStoreSelected(store: StoreDTO): void {
        if (this.containsById(this.selectedStores, store.id)) {
            this.showError('pages.stock.export.storeAlreadySelected');
            return;
        }
        this.selectedStores = [...this.selectedStores, store];
        this.storeSearcher?.clearSearch();
    }

    onProductSelected(product: StockLotProduct): void {
        if (this.selectedProducts.some(p => p.productId === product.productId)) {
            this.showError('pages.stock.export.productAlreadySelected');
            return;
        }
        this.selectedProducts = [...this.selectedProducts, product];
        this.productSearcher?.clearSearch();
    }

    removeStore(store: StoreDTO): void {
        this.selectedStores = this.selectedStores.filter(s => s.id !== store.id);
    }

    removeProduct(product: StockLotProduct): void {
        this.selectedProducts = this.selectedProducts.filter(p => p.productId !== product.productId);
    }

    trackByStoreId(_index: number, store: StoreDTO): number {
        return store.id;
    }

    trackByProductId(_index: number, product: StockLotProduct): number {
        return product.productId;
    }

    downloadFile(): void {
        if (this.isExporting) {
            return;
        }
        this.hasAttemptedExport = true;

        const validation = this.validateSelection();
        if (!validation.isValid) {
            this.showError(validation.messageKey);
            return;
        }

        this.isExporting = true;
        this.storeStockLotsService
            .exportStoreStockLots(this.buildExportParams())
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.isExporting = false;
                })
            )
            .subscribe({
            next: (items: StoreStockLotExportItemResponse) => {
                if (!items || !items.items || items.items.length === 0) {
                    this.snackbarService.showInfo(this.translateService.instant('pages.stock.export.emptyResult'));
                    return;
                }
                const csv = this.buildLocalizedCsv(items.items);
                const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' });
                const fileName = this.buildFileName();
                this.triggerDownload(blob, fileName);
                this.snackbarService.showSuccess(this.translateService.instant('pages.stock.export.downloadSuccess'));
            },
            error: () => {
                this.showError('pages.stock.export.downloadError');
            }
            });
    }

    // =========================
    // Private
    // =========================

    private applyContext(data: ExportStockDrawerData): void {
        this.isStorageDetailContext = !!data?.isStorageDetailContext;

        this.currentStoreId = typeof data?.storeId === 'number' ? data.storeId : null;
        this.currentStoreName = typeof data?.storeName === 'string' ? data.storeName : null;
        this.currentSearchText = typeof data?.searchText === 'string' ? data.searchText : null;

        if (this.isStorageDetailContext) {
            this.selectedExportType = 'all';
        }
    }

    private loadStores(): void {
        this.isLoadingStores = true;
        this.productsService
            .getStores()
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.isLoadingStores = false;
                })
            )
            .subscribe({
            next: (stores: StoreDTO[]) => {
                this.availableStores = Array.isArray(stores) ? stores : [];
                this.storeSearcher?.setResults(this.availableStores);
            },
            error: () => {
                this.showError('pages.stock.export.storesLoadError');
            }
            });
    }

    private loadStockLotProducts(): void {
        this.isLoadingProducts = true;
        this.productsService
            .getStockLotProducts()
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.isLoadingProducts = false;
                })
            )
            .subscribe({
            next: (products: StockLotProduct[]) => {
                this.availableProducts = Array.isArray(products) ? products : [];
                this.productSearcher?.setResults(this.availableProducts);
            },
            error: () => {
                this.showError('pages.stock.export.productSearchError');
            }
            });
    }

    private validateSelection(): { isValid: boolean; messageKey: string } {
        if (this.selectedExportType === 'store') {
            if (this.selectedStores.length === 0 && !this.currentStoreId) {
                return { isValid: false, messageKey: 'pages.stock.export.validation.storeRequired' };
            }
        }

        if (this.selectedExportType === 'product' && this.selectedProducts.length === 0) {
            return { isValid: false, messageKey: 'pages.stock.export.validation.productRequired' };
        }

        return { isValid: true, messageKey: '' };
    }

    get storeSelectionRequired(): boolean {
        return this.selectedExportType === 'store' && !this.isStorageDetailContext;
    }

    get productSelectionRequired(): boolean {
        return this.selectedExportType === 'product';
    }

    get showStoreError(): boolean {
        return this.hasAttemptedExport && this.storeSelectionRequired && this.selectedStores.length === 0 && !this.currentStoreId;
    }

    get showProductError(): boolean {
        return this.hasAttemptedExport && this.productSelectionRequired && this.selectedProducts.length === 0;
    }

    get canExport(): boolean {
        return !this.isExporting && this.validateSelection().isValid;
    }

    get selectedStoreSummary(): string | null {
        if (this.selectedStores.length > 0) {
            return this.selectedStores.map(store => store.name).join(', ');
        }
        return this.currentStoreName ?? null;
    }

    get selectedProductSummary(): string | null {
        if (this.selectedProducts.length > 0) {
            return this.selectedProducts
                .map(product => product.productName)
                .filter(Boolean)
                .join(', ');
        }
        const searchText = this.normalizeSearchText(this.currentSearchText);
        return searchText ?? null;
    }

    private getSelectedStoreIds(): number[] {
        if (this.isStorageDetailContext && this.currentStoreId != null) {
            return [this.currentStoreId];
        }
        if (this.selectedStores.length > 0) {
            return this.selectedStores.map(s => s.id);
        }
        return [];
    }

    private getSelectedProductSearchText(): string | undefined {
        const product = this.selectedProducts[0];
        const searchText = product?.productName;
        return this.normalizeSearchText(searchText);
    }

    private normalizeSearchText(value: string | null | undefined): string | undefined {
        const normalized = value?.trim();
        return normalized ? normalized : undefined;
    }

    private buildExportParams(): { storeIds?: number[]; productIds?: number[]; searchText?: string } {
        const storeIds = this.getSelectedStoreIds();
        const productIds = this.selectedProducts.map(p => p.productId).filter(id => typeof id === 'number');

        if (this.isStorageDetailContext) {
            return {
                storeIds: storeIds.length > 0 ? storeIds : undefined,
                productIds: productIds.length > 0 ? productIds : undefined,
                searchText: this.normalizeSearchText(this.currentSearchText)
            };
        }

        if (this.selectedExportType === 'store') {
            return {
                storeIds: storeIds.length > 0 ? storeIds : undefined
            };
        }

        if (this.selectedExportType === 'product') {
            return {
                storeIds: storeIds.length > 0 ? storeIds : undefined,
                productIds: productIds.length > 0 ? productIds : undefined
            };
        }

        return {
            storeIds: storeIds.length > 0 ? storeIds : undefined,
            productIds: productIds.length > 0 ? productIds : undefined,
            searchText: this.normalizeSearchText(this.currentSearchText)
        };
    }

    private buildFileName(): string {
        const dateStamp = new Date().toISOString().slice(0, 10);
        const storeName = this.selectedStores[0]?.name ?? this.currentStoreName;
        const searchText = this.getSelectedProductSearchText() ?? this.normalizeSearchText(this.currentSearchText);

        const baseName = this.translateService.instant('pages.stock.export.fileName');

        const parts = [baseName];
        if (storeName) parts.push(this.slugifyFileName(storeName));
        if (searchText) parts.push(this.slugifyFileName(searchText));
        parts.push(dateStamp);

        return `${parts.join('_')}.csv`;
    }

    private slugifyFileName(value: string): string {
        return value
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)+/g, '');
    }

    private triggerDownload(blob: Blob, fileName: string): void {
        const a = document.createElement('a');
        const objectUrl = URL.createObjectURL(blob);

        a.href = objectUrl;
        a.download = fileName;
        a.click();

        setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    }

    private containsById<T extends { id: number }>(items: T[], id: number): boolean {
        return items.some(i => i.id === id);
    }

    private showError(translationKey: string): void {
        this.snackbarService.showError(this.translateService.instant(translationKey), 'error');
    }

    private buildLocalizedCsv(items: StoreStockLotExportItem[]): string {
        const t = (key: string) => this.translateService.instant(key);
        const columns: CsvColumn<StoreStockLotExportItem>[] = [
            { header: t('pages.stock.export.csvHeaders.store'), value: r => r.storeName },
            { header: t('pages.stock.export.csvHeaders.ean'), value: r => r.ean },
            { header: t('pages.stock.export.csvHeaders.productCode'), value: r => r.productCode },
            { header: t('pages.stock.export.csvHeaders.brand'), value: r => r.brand },
            { header: t('pages.stock.export.csvHeaders.model'), value: r => r.model },
            { header: t('pages.stock.export.csvHeaders.season'), value: r => r.season },
            { header: t('pages.stock.export.csvHeaders.quality'), value: r => r.quality },
            { header: t('pages.stock.export.csvHeaders.width'), value: r => r.width },
            { header: t('pages.stock.export.csvHeaders.height'), value: r => r.height },
            { header: t('pages.stock.export.csvHeaders.radius'), value: r => r.radius },
            { header: t('pages.stock.export.csvHeaders.loadIndex'), value: r => r.loadIndex },
            { header: t('pages.stock.export.csvHeaders.speedIndex'), value: r => r.speedIndex },
            { header: t('pages.stock.export.csvHeaders.stock'), value: r => r.stock },
            // { header: t('pages.stock.export.csvHeaders.price'), value: r => r.price },
            { header: t('pages.stock.export.csvHeaders.currency'), value: r => r.currency },
            { header: t('pages.stock.export.csvHeaders.locations'), value: r => (Array.isArray(r.locations) ? r.locations.join(' | ') : '') },
            { header: t('pages.stock.export.csvHeaders.description'), value: r => r.description },
            { header: t('pages.stock.export.csvHeaders.family'), value: r => r.family ?? '' },
            { header: t('pages.stock.export.csvHeaders.wetGrip'), value: r => r.wetGrip },
            { header: t('pages.stock.export.csvHeaders.noiseLevel'), value: r => r.noiseLevel },
            // { header: t('pages.stock.export.csvHeaders.ecoTax'), value: r => r.ecoTaxAmount },
            // { header: t('pages.stock.export.csvHeaders.deliveryTime'), value: r => r.deliveryTime },
            { header: t('pages.stock.export.csvHeaders.image'), value: r => r.imageUrl }
        ];

        return buildCsv(items, columns);
    }
}
